'use client';

import React from 'react';
import { Select, MenuItem } from '@mui/material';
import { DayAvailability } from '../../../shared/models';
import { format } from 'date-fns';
import { trackEvent } from '../lib/analytics';

interface SlotAvailabilitySelectorProps {
    dayAvailability: DayAvailability;
    onSlotSelect: (slot: { StartTime: Date; EndTime: Date }) => void;
    selectedSlot?: { StartTime: Date; EndTime: Date };
}

export const SlotAvailabilitySelector: React.FC<SlotAvailabilitySelectorProps> = ({
    dayAvailability,
    onSlotSelect,
    selectedSlot
}) => {
    if (!dayAvailability.Slots || dayAvailability.Slots.length === 0) {
        return null;
    }

    const formatTimeSlot = (slot: { StartTime: Date; EndTime: Date }) => {
        return `${format(new Date(slot.StartTime), 'h:mm a')} - ${format(new Date(slot.EndTime), 'h:mm a')}`;
    };

    const handleSlotChange = (e: any) => {
        const selectedIndex = parseInt(e.target.value);
        const slot = dayAvailability.Slots![selectedIndex];

        // Track slot selection in Mixpanel (especially for mobile)
        trackEvent('Venue Time Slot Selected', {
            day: format(new Date(slot.StartTime), 'EEEE, MMMM d, yyyy'),
            time_slot: formatTimeSlot(slot),
            slots_available: dayAvailability.Slots!.length,
            platform: window.innerWidth <= 768 ? 'mobile' : 'desktop'
        });

        onSlotSelect(slot);
    };

    return (
        <Select
            value={dayAvailability.Slots.findIndex(
                slot =>
                    slot.StartTime === selectedSlot?.StartTime &&
                    slot.EndTime === selectedSlot?.EndTime
            ).toString()}
            onChange={handleSlotChange}
            fullWidth
            size="medium"
            sx={{
                height: '48px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: 'white',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    borderWidth: '1px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C0C0DD',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C0C0DD',
                    borderWidth: '2px',
                },
                '& .MuiSelect-icon': {
                    color: 'rgba(255, 255, 255, 0.8)',
                },
            }}
        >
            <MenuItem value="-1" sx={{
                fontSize: '0.95rem',
                padding: '12px 16px',
                '&:hover': {
                    backgroundColor: 'rgba(224, 177, 241, 0.08)'
                }
            }}>
                Select a time slot
            </MenuItem>
            {dayAvailability.Slots.map((slot, index) => (
                <MenuItem
                    key={index}
                    value={index}
                    sx={{
                        fontSize: '0.95rem',
                        padding: '12px 16px',
                        '&:hover': {
                            backgroundColor: 'rgba(224, 177, 241, 0.08)'
                        }
                    }}
                >
                    {formatTimeSlot(slot)}
                </MenuItem>
            ))}
        </Select>
    );
};
